import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, CardContent, Chip, Typography, Alert, Button, Stack } from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { fetchAppInfo, fetchHealthStatus, fetchSyncStatus } from '../../store/slices/appSlice';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const SystemStatus: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const { appInfo, healthStatus, syncStatus, loading } = useSelector((state: RootState) => state.app);

  const isAdministrator = useMemo(() => {
    const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
    const tokenParsed: any = keycloak?.tokenParsed || {};
    const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [] as any;
    // Policy: Only group membership confers admin rights
    const hasGroup = Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
    return hasGroup;
  }, [keycloak]);

  useEffect(() => {
    if (!isAdministrator) return;
    dispatch(fetchAppInfo());
    dispatch(fetchHealthStatus());
    dispatch(fetchSyncStatus());
  }, [dispatch, isAdministrator]);

  if (!isAdministrator) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You do not have permission to view this page. Administrator access is required.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>Go to Dashboard</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Application & System
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Application
          </Typography>
          {loading && !appInfo ? (
            <Typography variant="body2" color="textSecondary">Loading application information...</Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary">
                <strong>Message:</strong> {appInfo?.message || '—'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <strong>Version:</strong> {appInfo?.version || '—'}
              </Typography>
              {appInfo?.docs && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  <strong>Documentation:</strong> {appInfo.docs}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Health
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {healthStatus ? (
              <Chip
                label={healthStatus.status}
                color={healthStatus.status === 'healthy' ? 'success' : 'error'}
                variant="outlined"
              />
            ) : (
              <Typography variant="body2" color="textSecondary">Loading health status...</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sync Worker Metrics
          </Typography>
          {!syncStatus ? (
            <Typography variant="body2" color="textSecondary">Loading sync metrics...</Typography>
          ) : (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary"><strong>Status:</strong></Typography>
                <Chip label={syncStatus.status || 'unknown'} color={(syncStatus.status === 'healthy') ? 'success' : (syncStatus.status === 'degraded' ? 'warning' : 'default')} variant="outlined" />
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Queues</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`Bills: ${syncStatus.queues?.bill ?? '—'}`} variant="outlined" />
                <Chip label={`Members: ${syncStatus.queues?.member ?? '—'}`} variant="outlined" />
                <Chip label={`Maintenance: ${syncStatus.queues?.maintenance ?? '—'}`} variant="outlined" />
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Database Activity (last 24h)</Typography>
              <Typography variant="body2" color="textSecondary">
                Bills — New: {syncStatus.bills?.new_synced_last_24h ?? 0}, Updated: {syncStatus.bills?.updated_last_24h ?? 0}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Latest Bill Update: {syncStatus.bills?.last_updated_at || '—'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Members — Updated: {syncStatus.members?.updated_last_24h ?? 0}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Latest Member Update: {syncStatus.members?.last_updated_at || '—'}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Watermarks</Typography>
              <Box sx={{ pl: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Bills:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                  {syncStatus.watermarks?.bill && Object.keys(syncStatus.watermarks.bill).length > 0 ? (
                    Object.entries(syncStatus.watermarks.bill).map(([shard, wm]: any) => (
                      <Chip key={`wm-bill-${shard}`} label={`${shard}: ${wm}`} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                  )}
                </Stack>
                <Typography variant="body2" color="textSecondary">
                  House Votes:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {syncStatus.watermarks?.house_vote && Object.keys(syncStatus.watermarks.house_vote).length > 0 ? (
                    Object.entries(syncStatus.watermarks.house_vote).map(([shard, wm]: any) => (
                      <Chip key={`wm-vote-${shard}`} label={`${shard}: ${wm}`} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                  )}
                </Stack>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                <strong>Last Incremental Sync Anchor:</strong> {syncStatus.last_sync || '—'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemStatus;
